import { Compartment, EditorState } from "@codemirror/state";
import { forceParsing, syntaxTree } from "@codemirror/language";
import { EditorView, ViewPlugin } from "@codemirror/view";

import { atomicDecorations } from "./atomic-decorations.js";
import { commandTooltip } from "./command-tooltip.js";
import { effectListeners } from "./effect-listeners.js";
import { figureModal, figureModalPasteHandler } from "./figure-modal.js";
import { hasLanguageLoadedEffect } from "./language.js";
import { listItemMarker } from "./list-item-marker.js";
import { markDecorations } from "./mark-decorations.js";
import { mousedown, mouseDownEffect } from "./selection.js";
import { pasteHtml } from "./paste-html.js";
import { selectDecoratedArgument } from "./select-decorated-argument.js";
import { skipPreambleWithCursor } from "./skip-preamble-cursor.js";
import { restoreScrollPosition, scrollPosition } from "./scroll-position.js";
import { tableGeneratorTheme } from "./table-generator.js";
import { visualKeymap } from "./visual-keymap.js";
import { isVisual, toggleVisualEffect, visualState } from "./visual-state.js";
import { visualHighlightStyle, visualTheme } from "./visual-theme.js";
import { preambleCollapsedState } from "./visual-widgets/preamble.js";

export { isVisual, toggleVisualEffect };

const visualConf = new Compartment();
const parsedAttributesConf = new Compartment();

const visualEditorClass = EditorView.editorAttributes.of({
    class: "manuscript-visual-mode",
});

const visualContentAttributes = EditorView.contentAttributes.of({
    "aria-label": "Visual Editor editing",
});

const showContentWhenParsed = [
    parsedAttributesConf.of([EditorView.editable.of(false)]),
    ViewPlugin.define((view) => {
        const showContent = () => {
            view.dispatch(
                {
                    effects: parsedAttributesConf.reconfigure([
                        EditorView.editorAttributes.of({
                            class: "ol-cm-parsed",
                        }),
                        EditorView.editable.of(true),
                    ]),
                },
                restoreScrollPosition(),
            );
            view.focus();
        };

        if (syntaxTree(view.state).length === view.state.doc.length) {
            window.setTimeout(showContent);
            return {};
        }

        const fallbackTimer = window.setTimeout(showContent, 5000);
        let languageLoaded = false;

        return {
            update(update) {
                if (!languageLoaded && hasLanguageLoadedEffect(update)) {
                    languageLoaded = true;
                    window.setTimeout(() => {
                        Promise.resolve()
                            .then(() => {
                                forceParsing(view, view.state.doc.length, Infinity);
                                window.clearTimeout(fallbackTimer);
                                window.setTimeout(showContent);
                            })
                            .catch(() => {});
                    });
                }
            },
        };
    }),
];

const scrollJumpAdjuster = EditorState.transactionExtender.of((transaction) => {
    if (!transaction.scrollIntoView) {
        for (const effect of transaction.effects) {
            if (effect.is(mouseDownEffect) && effect.value === false) {
                return {
                    effects: EditorView.scrollIntoView(transaction.newSelection.main.head),
                };
            }
        }
    }

    return {};
});

function extension(options) {
    const atomicDecorationsField = atomicDecorations(options || {});

    return [
        visualTheme,
        visualHighlightStyle,
        visualEditorClass,
        mousedown,
        listItemMarker,
        atomicDecorationsField,
        selectDecoratedArgument(atomicDecorationsField),
        markDecorations,
        visualKeymap,
        commandTooltip,
        scrollJumpAdjuster,
        showContentWhenParsed,
        effectListeners(),
        skipPreambleWithCursor(atomicDecorationsField),
        pasteHtml,
        tableGeneratorTheme,
        figureModal(),
        figureModalPasteHandler(),
        preambleCollapsedState,
        visualContentAttributes,
        ...(options?.docId ? scrollPosition(options.docId, { visual: options.visual }) : []),
    ];
}

function configureVisualExtensions(options) {
    return options.visual ? extension(options) : [];
}

export function manuscriptVisual(options) {
    return [visualState.init(() => options.visual), visualConf.of(configureVisualExtensions(options))];
}

export function setVisual(options) {
    return {
        effects: [toggleVisualEffect.of(options.visual), visualConf.reconfigure(configureVisualExtensions(options))],
    };
}

export function sourceOnly(visual, extensionValue) {
    const compartment = new Compartment();
    const configure = (enabled) => (enabled ? [] : extensionValue);

    return [
        compartment.of(configure(visual)),
        EditorState.transactionExtender.of((transaction) => {
            for (const effect of transaction.effects) {
                if (effect.is(toggleVisualEffect)) {
                    return {
                        effects: compartment.reconfigure(configure(effect.value)),
                    };
                }
            }

            return null;
        }),
    ];
}
